<template>
    <div class="content-wrapper">
        <app-page-top-section title="Custom Invoices" icon="settings">
            <app-default-button
                title="Create Invoice"
                @click="openInvoiceModal()"
            />
        </app-page-top-section>

        <app-table
            id="custom-invoice-table"
            :options="options"
            @action="triggerActions"
        />

        <app-custom-invoice-modal
            v-if="isInvoiceModalActive"
            v-model="isInvoiceModalActive"
            :selected-url="selectedUrl"
        />

        <app-confirmation-modal
            v-if="confirmationModalActive"
            icon="trash-2"
            modal-class="danger"
            message="Are you sure you want to delete this invoice?"
            modal-id="app-confirmation-modal"
            @confirmed="deleteInvoice"
            @cancelled="cancelled"
        />
    </div>
</template>

<script>
import { CUSTOM_INVOICES } from "../../../Config/ApiUrl";
import { urlGenerator } from "../../../../common/Helper/AxiosHelper";
import { formatDateToLocal } from "../../../../common/Helper/Support/DateTimeHelper";
import DatatableHelperMixin from "../../../../common/Mixin/Global/DatatableHelperMixin";

export default {
    name: "CustomInvoices",
    mixins: [DatatableHelperMixin],
    data() {
        return {
            CUSTOM_INVOICES,
            isInvoiceModalActive: false,
            confirmationModalActive: false,
            selectedUrl: '',
            invoiceId: '',
            options: {
                name: 'Custom Invoices',
                url: CUSTOM_INVOICES,
                showHeader: true,
                showCount: true,
                showClearFilter: true,
                columns: [
                    {
                        title: 'Invoice Number',
                        type: 'text',
                        key: 'invoice_number',
                        isVisible: true,
                    },
                    {
                        title: 'Date',
                        type: 'custom-html',
                        key: 'invoice_date',
                        isVisible: true,
                        modifier: (value) => {
                            return formatDateToLocal(value);
                        }
                    },
                    {
                        title: 'Customer Name',
                        type: 'text',
                        key: 'customer_name',
                        isVisible: true,
                    },
                    {
                        title: 'Mobile',
                        type: 'text',
                        key: 'customer_mobile',
                        isVisible: true,
                    },
                    {
                        title: 'Project Name',
                        type: 'text',
                        key: 'project_name',
                        isVisible: true,
                    },
                    {
                        title: 'Amount',
                        type: 'custom-html',
                        key: 'amount',
                        isVisible: true,
                        modifier: (value) => {
                            return parseFloat(value).toFixed(2);
                        }
                    },
                    {
                        title: 'Discount',
                        type: 'custom-html',
                        key: 'discount',
                        isVisible: true,
                        modifier: (value) => {
                            return parseFloat(value || 0).toFixed(2);
                        }
                    },
                    {
                        title: 'Total Amount',
                        type: 'custom-html',
                        key: 'total_amount',
                        isVisible: true,
                        modifier: (value) => {
                            return `<strong>${parseFloat(value).toFixed(2)}</strong>`;
                        }
                    },
                    {
                        title: 'Payment Method',
                        type: 'text',
                        key: 'payment_method',
                        isVisible: true,
                    },
                    {
                        title: 'Actions',
                        type: 'component',
                        componentName: 'app-custom-invoice-action',
                        key: 'id',
                        isVisible: true
                    }
                ],
                filters: [
                    {
                        title: 'Date',
                        type: "range-picker",
                        key: "date",
                        option: ["today", "thisMonth", "last7Days", "thisYear"]
                    }
                ],
                paginationType: "pagination",
                responsive: true,
                rowLimit: 10,
                showAction: false,
                orderBy: 'desc',
                actionType: "dropdown",
            }
        }
    },
    created() {
        this.$hub.$on('custom-invoice-print', (row) => this.triggerActions(row, {name: 'print'}));
        this.$hub.$on('custom-invoice-download', (row) => this.triggerActions(row, {name: 'download'}));
        this.$hub.$on('custom-invoice-edit', (row) => this.triggerActions(row, {name: 'edit'}));
        this.$hub.$on('custom-invoice-delete', (row) => this.triggerActions(row, {name: 'delete'}));
    },
    beforeDestroy() {
        this.$hub.$off('custom-invoice-print');
        this.$hub.$off('custom-invoice-download');
        this.$hub.$off('custom-invoice-edit');
        this.$hub.$off('custom-invoice-delete');
    },
    watch: {
        isInvoiceModalActive: function (value) {
            if (!value) {
                this.selectedUrl = '';
            }
        }
    },
    methods: {
        openInvoiceModal() {
            this.selectedUrl = '';
            this.isInvoiceModalActive = true;
        },
        triggerActions(row, action, active) {
            this.invoiceId = row.id;
            if (action.name === 'edit') {
                this.selectedUrl = `${CUSTOM_INVOICES}/${row.id}`;
                this.isInvoiceModalActive = true;
            } else if (action.name === 'delete') {
                this.confirmationModalActive = true;
            } else if (action.name === 'print') {
                window.open(urlGenerator(`settings/custom-invoices/${row.id}/print`), '_blank');
            } else if (action.name === 'download') {
                window.open(urlGenerator(`settings/custom-invoices/${row.id}/download`), '_blank');
            }
        },
        deleteInvoice() {
            axios.delete(urlGenerator(`${CUSTOM_INVOICES}/${this.invoiceId}`))
                .then(({data}) => {
                    this.toastAndReload(data.message, 'custom-invoice-table');
                })
                .catch(({response}) => {
                    this.$toastr.e(response.data.message);
                })
                .finally(() => {
                    this.confirmationModalActive = false;
                });
        },
        cancelled() {
            this.confirmationModalActive = false;
        }
    }
}
</script>

<style scoped>
::v-deep .btn-print-action {
    color: #28a745 !important;
}
::v-deep .btn-print-action:hover {
    background-color: rgba(40, 167, 69, 0.1) !important;
    color: #28a745 !important;
}

::v-deep .btn-download-action {
    color: #007bff !important;
}
::v-deep .btn-download-action:hover {
    background-color: rgba(0, 127, 255, 0.1) !important;
    color: #007bff !important;
}

::v-deep .btn-edit-action {
    color: #ffc107 !important;
}
::v-deep .btn-edit-action:hover {
    background-color: rgba(255, 193, 7, 0.1) !important;
    color: #ffc107 !important;
}

::v-deep .btn-delete-action {
    color: #dc3545 !important;
}
::v-deep .btn-delete-action:hover {
    background-color: rgba(220, 53, 69, 0.1) !important;
    color: #dc3545 !important;
}

/* Dropdown menu items custom styles */
::v-deep .dropdown-item.btn-print-action {
    color: #28a745 !important;
}
::v-deep .dropdown-item.btn-print-action:hover {
    background-color: #28a745 !important;
    color: #fff !important;
}

::v-deep .dropdown-item.btn-download-action {
    color: #007bff !important;
}
::v-deep .dropdown-item.btn-download-action:hover {
    background-color: #007bff !important;
    color: #fff !important;
}

::v-deep .dropdown-item.btn-edit-action {
    color: #ffc107 !important;
}
::v-deep .dropdown-item.btn-edit-action:hover {
    background-color: #ffc107 !important;
    color: #fff !important;
}

::v-deep .dropdown-item.btn-delete-action {
    color: #dc3545 !important;
}
::v-deep .dropdown-item.btn-delete-action:hover {
    background-color: #dc3545 !important;
    color: #fff !important;
}
</style>
